import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Award, ShieldCheck, HeartHandshake, Clock, LifeBuoy, Compass, Sparkles, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { METER_META, TONE_STYLES, OUTCOME_TIERS, PRINCIPLES } from "@/lib/sim";
import { computeCaseScore } from "@/lib/progression";
import { fetchSession } from "@/lib/api";
import { DebriefBody } from "@/components/sim/SummaryScreen";

const ICONS = { ShieldCheck, HeartHandshake, Clock, LifeBuoy };

const TIER_ACCENT = {
  good: "from-emerald-700 to-emerald-800",
  caution: "from-amber-600 to-amber-700",
  harmful: "from-rose-600 to-rose-700",
};

export default function SessionReport() {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchSession(sessionId)
      .then(setSession)
      .catch(() => setError(true));
  }, [sessionId]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center px-4">
        <p data-testid="report-not-found" className="text-stone-500">
          This report could not be found. The link may be incorrect or the session was removed.
        </p>
      </div>
    );
  }
  if (!session) {
    return <div className="min-h-screen grid place-items-center text-stone-400">Loading report…</div>;
  }

  const tier = OUTCOME_TIERS[session.outcome_tier] || OUTCOME_TIERS.solid;
  const accent = TIER_ACCENT[tier.tone];
  const score = computeCaseScore(session.final_meters, session.choices);
  const date = new Date(session.created_at).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <div className="flex items-center justify-between mb-4 print:hidden">
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-400">
          Safe Path Simulator · Session Report
        </span>
        <Button
          data-testid="print-report-button"
          variant="outline"
          onClick={() => window.print()}
          className="rounded-full border-stone-300 text-stone-700 hover:bg-stone-50 h-9 px-4 text-sm"
        >
          <Printer className="mr-2 h-4 w-4" /> Print / save as PDF
        </Button>
      </div>

      <div data-testid="session-report" className="rounded-3xl border border-stone-200 bg-white shadow-sm overflow-hidden">
        <div className={`bg-gradient-to-br ${accent} px-6 sm:px-10 py-10 text-white`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-white/80 text-[11px] font-semibold uppercase tracking-[0.18em]">
                <Award className="h-4 w-4" /> {session.scenario_title}
                {session.ended_early ? " · ended early" : " · complete"}
              </div>
              <h1 data-testid="report-outcome-title" className="mt-3 font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
                {tier.title}
              </h1>
              <p className="mt-2 text-white/85 text-sm">
                {session.trainee_name && (
                  <span data-testid="report-trainee-name">Trainee: {session.trainee_name} · </span>
                )}
                Client: {session.client || "the client"} · Completed {date}
              </p>
            </div>
            <div data-testid="report-case-score" className="shrink-0 text-center rounded-2xl bg-white/15 backdrop-blur px-5 py-4 ring-1 ring-white/25">
              <div className="text-3xl sm:text-4xl font-heading font-semibold tabular-nums">{score}%</div>
              <div className="text-[11px] uppercase tracking-wider text-white/80 mt-1">Case score</div>
            </div>
          </div>
        </div>

        <div className="px-6 sm:px-10 py-8">
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-stone-500">Final meters</h3>
              <div className="mt-4 space-y-4">
                {["safety", "trust", "time", "resources"].map((id) => {
                  const meta = METER_META[id];
                  const Icon = ICONS[meta.icon];
                  return (
                    <div key={id}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="flex items-center gap-2 text-sm font-medium text-stone-700">
                          <Icon className={`h-4 w-4 ${meta.text}`} /> {meta.label}
                        </span>
                        <span className={`text-sm font-semibold tabular-nums ${meta.text}`}>
                          {Math.round(session.final_meters[id])}
                        </span>
                      </div>
                      <div className="h-2.5 w-full rounded-full bg-stone-200 overflow-hidden">
                        <div className={`h-full rounded-full ${meta.bar}`} style={{ width: `${session.final_meters[id]}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-stone-500">Decisions made</h3>
              <p className="mt-2 text-sm text-stone-500">
                {session.good_count} survivor-centered · {session.caution_count} mixed · {session.harmful_count} harmful
              </p>
              <div className="mt-3 space-y-2 max-h-64 overflow-y-auto pr-1 print:max-h-none print:overflow-visible">
                {session.choices.map((c) => {
                  const t = TONE_STYLES[c.tone];
                  return (
                    <div key={c.decision_id} className="flex items-start gap-2 text-sm">
                      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${t.dot}`} />
                      <span className="text-stone-600 leading-snug">
                        <span className="font-medium text-stone-800">{c.decision_title}</span>
                        <span className="text-stone-400"> · {c.choice_text}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {session.choices.some((c) => c.principle) && (
            <div className="mt-8 rounded-2xl border border-stone-200 bg-stone-50/60 p-6" data-testid="report-principles">
              <div className="flex items-center gap-2">
                <Compass className="h-5 w-5 text-stone-600" />
                <h3 className="font-heading text-lg font-semibold text-stone-900">Trauma-informed principles</h3>
              </div>
              <div className="mt-4">
                {PRINCIPLES.map((p) => {
                  const related = session.choices.filter((c) => c.principle === p.key);
                  if (!related.length) return null;
                  return (
                    <div key={p.key} className="flex items-start justify-between gap-4 py-2.5 border-b border-stone-200/70 last:border-0">
                      <div>
                        <span className="font-medium text-stone-900 text-sm">{p.label}</span>
                        <p className="text-xs text-stone-500 mt-0.5">{p.desc}</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 mt-1 flex-wrap justify-end max-w-[40%]">
                        {related.map((c) => (
                          <span key={c.decision_id} title={c.decision_title} className={`h-2.5 w-2.5 rounded-full ${TONE_STYLES[c.tone].dot}`} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {session.debrief && (
            <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-6" data-testid="report-debrief">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-emerald-700" />
                <h3 className="font-heading text-lg font-semibold text-stone-900">Reflection Coach debrief</h3>
              </div>
              <div className="mt-4">
                <DebriefBody text={session.debrief} />
              </div>
              <p data-testid="report-debrief-disclosure" className="mt-4 text-xs text-stone-400">
                This reflection summarizes your choices against the training material. It is not clinical guidance.
              </p>
            </div>
          )}

          <p className="mt-8 text-xs text-stone-400">
            This is a read-only training report generated by Safe Path Simulator. Scenarios are composites and the survivors are fictional.
          </p>
        </div>
      </div>
    </div>
  );
}
